/**
 * Utility for calculating matrix column widths based on alternatives count and text length
 */
export class MatrixColumnWidthUtil {
  private static maxLengthCache = new Map<string, number>();

  private static readonly MULTI_MATRIX_WIDTHS = new Map<number, number>([
    [1, 140],
    [2, 120],
    [3, 40],
    [4, 30],
    [5, 25],
  ]);

  private static readonly MATRIX_WIDTHS = new Map<number, number>([
    [1, 300],
    [2, 250],
    [3, 200],
    [4, 180],
    [5, 150],
  ]);

  private static readonly DEFAULT_MULTI_MATRIX_WIDTH = 20;
  private static readonly DEFAULT_MATRIX_WIDTH = 100;
  private static readonly DEFAULT_HEADER_LENGTH = 40;
  private static readonly TINY_HEADER_LENGTH = 10;

  /**
   * Calculate the width for each column based on the number of alternatives
   * and the longest alternative title length.
   * Returns a fixed pixel width for more condensed layout.
   * Doubles the width if the longest alternative title exceeds 40 characters.
   * @param type - 'multi-matrix' or 'matrix' to apply different width calculations
   */
  static getColumnWidth(
    alternativesCount: number,
    alternatives?: { title: string }[],
    questionId?: string,
    type: 'multi-matrix' | 'matrix' = 'matrix',
  ): string {
    const widthMap =
      type === 'multi-matrix' ? this.MULTI_MATRIX_WIDTHS : this.MATRIX_WIDTHS;
    const defaultWidth =
      type === 'multi-matrix'
        ? this.DEFAULT_MULTI_MATRIX_WIDTH
        : this.DEFAULT_MATRIX_WIDTH;

    let widthPerColumn = widthMap.get(alternativesCount) ?? defaultWidth;

    if (alternatives && alternatives.length > 0 && questionId) {
      let maxLength = this.maxLengthCache.get(questionId);

      if (maxLength === undefined) {
        maxLength = Math.max(
          ...alternatives.map((alt) => this.stripHtml(alt.title).length),
        );
        this.maxLengthCache.set(questionId, maxLength);
      }

      // if longest title exceeds default length, increase column width
      if (maxLength > this.DEFAULT_HEADER_LENGTH) {
        widthPerColumn *= type === 'multi-matrix' ? 5 : 2;
      }
    }

    return `${widthPerColumn}px`;
  }

  /**
   * Calculate the minimum width for each column based on the longest alternative title length.
   * Returns a fixed pixel min-width for better text display.
   * @param alternatives - Array of alternatives with title property
   * @param questionId - Unique question ID for caching
   */
  static getColumnMinWidth(title: string, questionId?: string): string {
    let minWidth = 20; // Default min-width

    if (title?.length > 2) {
      if (title.length < this.TINY_HEADER_LENGTH) {
        minWidth = 40;
      } else if (title.length < this.DEFAULT_HEADER_LENGTH) {
        minWidth = 60;
      } else {
        minWidth = 120;
      }
    }

    return `${minWidth}px`;
  }

  /**
   * Strip HTML tags from a string to get plain text length
   */
  private static stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  /**
   * Clear the cache (useful for testing or when data changes)
   */
  static clearCache(): void {
    this.maxLengthCache.clear();
  }
}
